import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { ReviewItem, GoogleStatus } from "../types";
import { StarRating } from "../StarRating";
import { ReviewStatusBadge, ResponseStatusBadge, SentimentBadge } from "../RiskBadge";
import { PlatformApiError, type PlatformApiClient } from "../../api";
import { REVIEW_FILTERS, defaultReviewFilter, latestResponse, reviewFilterFromSearch, type ReviewFilter } from "../review-filters";
import { originalReviewText, splitGoogleReviewText } from "../review-text";
import { REVIEWBOOST_V3_UI_ENABLED } from "../../review-boost-v3-ui";
import { useUnsavedReviewGuard } from "../use-unsaved-review-guard";
import { REVIEWBOOST_V4_UI_ENABLED } from "../../review-boost-v4-ui";
export { isReviewFilter, reviewFilterFromSearch } from "../review-filters";
export type { ReviewFilter } from "../review-filters";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (name.slice(0, 2) || "CD").toUpperCase();
}

function formatDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatAgo(isoString: string): string {
  try {
    const d = new Date(isoString);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return "Hoy";
    if (diffDays === 1) return "Hace 1 día";
    if (diffDays < 30) return `Hace ${diffDays} días`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return "Hace 1 mes";
    return `Hace ${diffMonths} meses`;
  } catch {
    return "Reciente";
  }
}

type PendingContextChange =
  | { kind: "navigate"; path: string }
  | { kind: "filter"; filter: ReviewFilter };

interface DraftState {
  identity: string | null;
  baseline: string;
  value: string;
}

export function ReviewsView({
  reviews,
  selectedId,
  initialFilter,
  slug,
  api,
  base,
  navigate,
  reload,
  googleConnected = false,
  googleStatus = null,
}: {
  reviews: ReviewItem[];
  selectedId: string | null;
  initialFilter?: ReviewFilter;
  slug: string;
  api: PlatformApiClient;
  base: string;
  navigate: (path: string) => void;
  reload: () => void;
  googleConnected?: boolean;
  googleStatus?: GoogleStatus["status"] | null;
}) {
  const requestedFilter = initialFilter
    || reviewFilterFromSearch(typeof window === "undefined" ? "" : window.location.search);
  const routeFilter = requestedFilter || defaultReviewFilter(reviews);

  const [currentFilter, setCurrentFilter] = useState<ReviewFilter>(() => routeFilter);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"recent" | "rating-low" | "rating-high">("recent");
  const [selectedTone, setSelectedTone] = useState("profesional");
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customInstruction, setCustomInstruction] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [publishConfirmation, setPublishConfirmation] = useState<{ reviewId: string; responseId: string; body: string } | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [busy, setBusy] = useState(false);
  const publishInFlightRef = useRef(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publicationNotice, setPublicationNotice] = useState<Record<string, string>>({});
  const [pendingContextChange, setPendingContextChange] = useState<PendingContextChange | null>(null);
  const [draft, setDraft] = useState<DraftState>({ identity: null, baseline: "", value: "" });
  const [viewOfflineReviews, setViewOfflineReviews] = useState(false);
  const needsConnection = googleStatus !== null && googleStatus !== "connected";
  const showGoogleGate = needsConnection && !viewOfflineReviews && !selectedId;

  // Keep keyboard users inside the active confirmation and return to its trigger.
  useEffect(() => {
    if (!pendingContextChange && !customModalOpen && !rejectModalOpen && !publishConfirmation) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = document.querySelector<HTMLElement>(".rep-modal-backdrop");
    if (!dialog) return;
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex="0"]'));
    (dialog.querySelector<HTMLElement>("[data-safe-focus]") || controls()[0])?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPendingContextChange(null); setCustomModalOpen(false); setRejectModalOpen(false); setPublishConfirmation(null);
      }
      if (event.key !== "Tab") return;
      const items = controls();
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    dialog.addEventListener("keydown", keydown);
    return () => { dialog.removeEventListener("keydown", keydown); if (previous?.isConnected) previous.focus(); };
  }, [pendingContextChange, customModalOpen, rejectModalOpen, publishConfirmation]);

  function notify(msg: string) {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  }

  useEffect(() => {
    setCurrentFilter((current) => current === routeFilter ? current : routeFilter);
  }, [routeFilter]);

  const counts = useMemo(() => {
    return Object.fromEntries(
      REVIEW_FILTERS.map(([key, , pred]) => [key, reviews.filter(pred).length])
    ) as Record<ReviewFilter, number>;
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    const filterPredicate = REVIEW_FILTERS.find(([key]) => key === currentFilter)?.[2] || (() => true);
    const term = searchTerm.trim().toLowerCase();

    return reviews
      .filter(filterPredicate)
      .filter((r) => !term || `${r.author} ${r.text || ""}`.toLowerCase().includes(term))
      .sort((a, b) => {
        if (sortBy === "rating-low") {
          return a.rating - b.rating || new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
        }
        if (sortBy === "rating-high") {
          return b.rating - a.rating || new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
        }
        return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
      });
  }, [reviews, currentFilter, searchTerm, sortBy]);

  const activeReview = useMemo(() => {
    // A missing deep-link ID must never silently display a different customer's review.
    if (selectedId) return reviews.find((r) => r.id === selectedId) || null;
    return filteredReviews[0] || null;
  }, [selectedId, reviews, filteredReviews]);
  const selectedOutsideFilter = Boolean(
    selectedId && activeReview && !filteredReviews.some((review) => review.id === selectedId)
  );

  const activeResponse = activeReview ? latestResponse(activeReview) : null;
  const uncertainPublication = activeReview ? publicationNotice[activeReview.id] : undefined;
  const isDiscarded = activeResponse?.status === "rechazada";
  const editableResponse = activeResponse && !isDiscarded ? activeResponse : null;
  // The API can persist error_publicacion; keep retry closed after a full reload.
  // Pending/mismatch outcomes still require authoritative server reconciliation.
  const serverPublicationNeedsReview = REVIEWBOOST_V3_UI_ENABLED && editableResponse?.status === "error_publicacion";
  const reviewPublished = activeReview?.public_response_status === "publicada" || editableResponse?.status === "publicada";
  const draftIdentity = editableResponse && activeReview
    ? `${activeReview.id}:${editableResponse.id}:${editableResponse.version}`
    : null;

  useEffect(() => {
    const canonicalBody = editableResponse?.body || "";
    setDraft({
      identity: draftIdentity,
      baseline: canonicalBody,
      value: canonicalBody,
    });
  }, [draftIdentity, editableResponse?.body]);

  const draftDirty = Boolean(
    editableResponse
      && draft.identity === draftIdentity
      && draft.value !== draft.baseline,
  );
  useUnsavedReviewGuard(draftDirty, slug);
  const responseJourneyStage = activeReview?.public_response_status === "publicada"
    || editableResponse?.status === "publicada"
    ? "published"
    : editableResponse
      ? "review"
      : "draft";

  function performContextChange(change: PendingContextChange) {
    setActionError(null);
    if (change.kind === "navigate") {
      navigate(change.path);
      return;
    }

    setCurrentFilter(change.filter);
    setSearchTerm("");
    navigate(`${base}/reviews?tab=${encodeURIComponent(change.filter)}`);
  }

  function requestContextChange(change: PendingContextChange) {
    if (change.kind === "filter" && change.filter === currentFilter) return;
    if (draftDirty) {
      setPendingContextChange(change);
      return;
    }
    performContextChange(change);
  }

  function abandonLocalDraftAndContinue() {
    const change = pendingContextChange;
    setPendingContextChange(null);
    if (!change) return;
    setDraft((current) => ({ ...current, value: current.baseline }));
    performContextChange(change);
  }

  function resetLocalDraft() {
    setDraft((current) => ({ ...current, value: current.baseline }));
    setActionError(null);
  }

  async function generateDraft(reviewId: string, tone = selectedTone) {
    setBusy(true);
    setActionError(null);
    try {
      await api.reputation(slug, `/reviews/${encodeURIComponent(reviewId)}/ai-draft`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tone }),
      });
      notify("Respuesta generada con IA.");
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "No se pudo generar la respuesta.");
    } finally {
      setBusy(false);
    }
  }

  async function reviseDraft(responseId: string, instruction: string) {
    if (draftDirty) {
      setActionError("Guardá o descartá tus cambios locales antes de pedir un nuevo ajuste con IA.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await api.reputation(slug, `/review-responses/${encodeURIComponent(responseId)}/revise`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ instruction }),
      });
      notify("Nueva versión generada con IA.");
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "No se pudo revisar la respuesta.");
    } finally {
      setBusy(false);
    }
  }

  async function saveResponseEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editableResponse) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.reputation(slug, `/responses/${encodeURIComponent(editableResponse.id)}/edit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ body: draft.value }),
      });
      setDraft((current) => ({ ...current, baseline: current.value }));
      notify("Edición guardada correctamente.");
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "No se pudo guardar la edición.");
    } finally {
      setBusy(false);
    }
  }

  function requestPublishConfirmation(responseId: string, bodyText: string) {
    if (!googleConnected || reviewPublished || busy || uncertainPublication || serverPublicationNeedsReview) {
      setActionError(reviewPublished ? "Esta reseña ya está publicada en Google." : (uncertainPublication || serverPublicationNeedsReview) ? "Ya existe un resultado de publicación que requiere comprobación. No reenvíes la respuesta sin verificar el estado en Google." : "La publicación no está disponible en este momento. Verificá la conexión de Google.");
      return;
    }
    if (draftDirty || draft.identity !== draftIdentity) {
      setActionError("Guardá la edición antes de revisar el texto para publicar.");
      return;
    }
    if (!activeReview || !bodyText.trim()) {
      setActionError("La respuesta a publicar no puede estar vacía.");
      return;
    }
    setActionError(null);
    setPublishConfirmation({ reviewId: activeReview.id, responseId, body: bodyText.trim() });
  }

  function confirmManualPublication() {
    const choice = publishConfirmation;
    if (!choice || publishInFlightRef.current) return;
    if (busy || reviewPublished || serverPublicationNeedsReview || uncertainPublication || choice.reviewId !== activeReview?.id || choice.responseId !== editableResponse?.id || draftDirty || draft.identity !== draftIdentity || choice.body !== editableResponse?.body.trim()) {
      setPublishConfirmation(null);
      setActionError("La reseña o el borrador cambió. Revisá de nuevo antes de publicar.");
      return;
    }
    // React state updates may not disable the dialog between two click events.
    // Acquire a synchronous UI lock before the provider-facing request starts.
    publishInFlightRef.current = true;
    setPublishConfirmation(null);
    void publishResponse(choice.responseId, choice.body).finally(() => {
      publishInFlightRef.current = false;
    });
  }

  async function publishResponse(responseId: string, bodyText: string) {
    if (!googleConnected) {
      setActionError("Conectá Google Business Profile desde Configuración para publicar directamente.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const outcome = await api.reputation<{ google_verification?: { status: "confirmed" | "pending" | "mismatch" | "not_applicable" } }>(slug, `/responses/${encodeURIComponent(responseId)}/publish`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ body: bodyText }),
      });
      setDraft((current) => ({ ...current, baseline: current.value }));
      const verification = outcome.google_verification?.status;
      if (verification === "confirmed") {
        notify("Respuesta publicada y verificada en Google.");
      } else if (REVIEWBOOST_V3_UI_ENABLED && activeReview) {
        setPublicationNotice((current) => ({ ...current, [activeReview.id]: verification === "mismatch"
          ? "La respuesta enviada no coincide con la lectura de Google. No vuelvas a publicar. Comprobá el estado antes de continuar."
          : "La publicación todavía no está verificada en Google. No vuelvas a publicar. Comprobá el estado antes de continuar." }));
        return;
      } else if (verification === "mismatch") {
        notify("Google aceptó la respuesta, pero la lectura posterior no coincide. No vuelvas a publicar; revisá Google.");
      } else {
        notify("Google aceptó la publicación. Su verificación puede tardar: no vuelvas a publicar.");
      }
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "No se pudo publicar la respuesta.");
      if (REVIEWBOOST_V3_UI_ENABLED && activeReview && !(cause instanceof PlatformApiError && cause.status >= 400 && cause.status < 500)) {
        setPublicationNotice((current) => ({ ...current, [activeReview.id]: "No pudimos confirmar el resultado del intento. Comprobá el estado antes de volver a publicar." }));
      }
    } finally {
      setBusy(false);
    }
  }

  async function rejectDraft(responseId: string) {
    setBusy(true);
    setActionError(null);
    try {
      await api.reputation(slug, `/responses/${encodeURIComponent(responseId)}/reject`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ note: rejectNote }),
      });
      setRejectModalOpen(false);
      setRejectNote("");
      setDraft({ identity: null, baseline: "", value: "" });
      notify("Borrador descartado. Podés generar una nueva versión.");
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "No se pudo rechazar la respuesta.");
    } finally {
      setBusy(false);
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      notify("Copiado al portapapeles.");
    } catch {
      notify("No se pudo copiar.");
    }
  }

  return (
    <div className="rep-reviews-view rep-layout-approved" data-layout="RB-RES-B" data-task={selectedId ? "detail" : "list"} data-google-gate={showGoogleGate ? "true" : "false"}>
      <header className="page-heading">
        <p className="eyebrow">Reputación pública</p>
        <h1>Reseñas</h1>
        <p style={{ color: "var(--cd-color-text-muted)" }}>
          Revisá lo que dicen tus clientes y respondé con claridad desde un solo lugar.
        </p>
      </header>


      {showGoogleGate && (
        <section className="rep-google-connection-gate" aria-label="Conexión necesaria para operar con Google" role="status">
          <div className="rep-google-gate-symbol" aria-hidden="true">G</div>
          <h2>{googleStatus === "expired" || googleStatus === "needs_reauth"
            ? "La autorización de Google venció"
            : googleStatus === "connecting" ? "Conexión de Google en curso"
              : "Conectá Google Business Profile"}</h2>
          <p>{googleStatus === "expired" || googleStatus === "needs_reauth"
            ? "Volvé a autorizar la cuenta para sincronizar y publicar respuestas."
            : googleStatus === "connecting"
              ? "Revisá el estado de conexión antes de publicar respuestas."
              : "Para sincronizar y publicar respuestas necesitás conectar la cuenta de Google de tu negocio."}</p>
          <div className="rep-google-gate-actions">
            <button type="button" className="primary-button" onClick={() => navigate(`${base}/settings/google`)}>
              {googleStatus === "expired" || googleStatus === "needs_reauth" ? "Reconectar Google" : "Ir a conectar Google"}
            </button>
            {reviews.length > 0 && (
              <button type="button" className="rep-inline-button" onClick={() => setViewOfflineReviews(true)}>
                Ver reseñas sin publicar
              </button>
            )}
          </div>
        </section>
      )}
      {needsConnection && !showGoogleGate && (
        <div className="rep-offline-workspace-notice" role="status">
          La cuenta de Google no está operativa. Podés consultar y preparar respuestas, pero no publicarlas.
          <button type="button" onClick={() => navigate(`${base}/settings/google`)}>Revisar conexión</button>
        </div>
      )}

      {toastMessage && (
        <div className="success-alert rep-floating-toast" role="status">
          {toastMessage}
        </div>
      )}

      {REVIEWBOOST_V4_UI_ENABLED && <label className="rbv4-compact-filter">
        <span>Mostrar</span>
        <select aria-label="Filtrar reseñas" value={currentFilter} onChange={event => requestContextChange({ kind: "filter", filter: event.target.value as ReviewFilter })}>
          {REVIEW_FILTERS.map(([key, label]) => <option key={key} value={key}>{label} · {counts[key]}</option>)}
        </select>
      </label>}
      <div className="rep-tabs-bar" role="group" aria-label="Filtrar reseñas">
        {REVIEW_FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={currentFilter === key}
            className={`rep-tab-button ${currentFilter === key ? "active" : ""}`}
            onClick={() => requestContextChange({ kind: "filter", filter: key })}
          >
            <span>{label}</span>
            <span className="rep-tab-count">{counts[key]}</span>
          </button>
        ))}
      </div>

      <p className="rbv3-mobile-scroll-hint">Deslizá para ver todos los filtros →</p>

      <div className="rep-workspace-split">
        <section className={`details-card rep-list-panel ${selectedId ? "hide-on-mobile" : ""}`} aria-label="Lista de reseñas">
          <div className="rep-panel-heading rep-list-heading">
            <div>
              <span className="rep-command-kicker">Cola de trabajo</span>
              <h2>{filteredReviews.length} {filteredReviews.length === 1 ? "reseña" : "reseñas"}</h2>
            </div>
            <span className="rep-panel-count">
              {currentFilter === "todas" ? "Todas" : REVIEW_FILTERS.find(([key]) => key === currentFilter)?.[1]}
            </span>
          </div>
          <div className="rep-list-toolbar">
            <label className="rep-search-box">
              <span className="sr-only">Buscar reseñas</span>
              <input
                type="search"
                placeholder="Buscar por cliente o comentario…"
                value={searchTerm}
                disabled={draftDirty}
                aria-describedby={draftDirty ? "rep-dirty-search-hint" : undefined}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
            <label className="rep-sort-box">
              <span className="sr-only">Ordenar reseñas</span>
              <select
                value={sortBy}
                disabled={draftDirty}
                aria-describedby={draftDirty ? "rep-dirty-search-hint" : undefined}
                onChange={(e) => setSortBy(e.target.value as "recent" | "rating-low" | "rating-high")}
              >
                <option value="recent">Más recientes</option>
                <option value="rating-low">Menor rating</option>
                <option value="rating-high">Mayor rating</option>
              </select>
            </label>
          </div>

          {draftDirty && (
            <p id="rep-dirty-search-hint" className="rep-hint-text" role="status">
              Hay cambios sin guardar. Guardalos o descartalos antes de cambiar búsqueda u orden.
            </p>
          )}

          <div className="rep-review-items">
            {filteredReviews.length === 0 ? (
              <div className="empty-state">
                <p>No hay reseñas en esta vista.</p>
                <p className="rep-hint-text">{reviews.length === 0 ? "Cuando lleguen reseñas de Google, aparecerán acá." : "Probá otro filtro o borrá la búsqueda para ver más resultados."}</p>
              </div>
            ) : (
              filteredReviews.map((r) => {
                const isSelected = activeReview?.id === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={`rep-review-row ${isSelected ? "selected" : ""}`}
                    aria-pressed={isSelected}
                    onClick={() => {
                      if (selectedId === r.id) return;
                      requestContextChange({
                        kind: "navigate",
                        path: `${base}/reviews/${encodeURIComponent(r.id)}?tab=${encodeURIComponent(currentFilter)}`,
                      });
                    }}
                  >
                    <span className={`rep-author-avatar ${r.rating <= 2 ? "danger" : ""}`}>
                      {initials(r.author)}
                    </span>
                    <div className="rep-row-content">
                      <div className="rep-row-head">
                        <strong>{r.author}</strong>
                        <ReviewStatusBadge
                          publicStatus={r.public_response_status}
                          workflowStatus={r.status}
                          riskLevel={r.risk_level}
                        />
                      </div>
                      <div className="rep-row-meta">
                        <StarRating rating={r.rating} size="sm" />
                        <span title={formatDate(r.published_at)}>Google · {formatAgo(r.published_at)}</span>
                      </div>
                      {r.text && <p className="rep-row-excerpt">{originalReviewText(r.text)}</p>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className={`details-card rep-detail-panel ${!selectedId ? "hide-on-mobile" : ""}`} aria-label="Detalle de reseña">
          {selectedId && (
            <div className="rep-mobile-back-row">
              <button
                type="button"
                className="rep-inline-button"
                onClick={() => requestContextChange({
                  kind: "navigate",
                  path: `${base}/reviews?tab=${encodeURIComponent(currentFilter)}`,
                })}
              >
                ← Volver a la lista de reseñas
              </button>
            </div>
          )}

          {actionError && <div className="inline-alert" role="alert">{actionError}</div>}

          {activeReview ? (
            <div className="rep-detail-content">
              {selectedOutsideFilter && (
                <p className="rep-hint-text" role="status">
                  Abriste esta reseña desde un enlace. No pertenece al filtro activo de la bandeja.
                </p>
              )}
              <div className="rep-detail-header">
                <div className="rep-review-author-row">
                  <span className={`rep-author-avatar large ${activeReview.rating <= 2 ? "danger" : ""}`}>
                    {initials(activeReview.author)}
                  </span>
                  <div>
                    <h2>{activeReview.author}</h2>
                    <div className="rep-author-meta">
                      <StarRating rating={activeReview.rating} size="md" />
                      <span>Google · {formatDate(activeReview.published_at)} ({formatAgo(activeReview.published_at)})</span>
                    </div>
                  </div>
                </div>
                <ReviewStatusBadge
                  publicStatus={activeReview.public_response_status}
                  workflowStatus={activeReview.status}
                  riskLevel={activeReview.risk_level}
                />
              </div>

              <div className="rep-quote-box">
                {REVIEWBOOST_V3_UI_ENABLED && <h3>Reseña original</h3>}
                <p>{originalReviewText(activeReview.text) || "Reseña calificada sin comentario de texto."}</p>
                {splitGoogleReviewText(activeReview.text).googleTranslation && (
                  <details className="rep-hint-text" style={{ marginTop: "12px" }}>
                    <summary>Ver traducción añadida por Google (idioma sin verificar)</summary>
                    <p style={{ whiteSpace: "pre-wrap" }}>{splitGoogleReviewText(activeReview.text).googleTranslation}</p>
                    <small>No es una traducción al idioma configurado por tu negocio.</small>
                  </details>
                )}
              </div>

              {activeReview.risk_level === "alto" && (
                <div className="rep-warning-box">
                  <span className="rep-warning-icon rep-warning-icon-risk" aria-hidden="true" />
                  <div>
                    <strong>Reseña sensible detectada</strong>
                    {activeReview.risk_reasons && activeReview.risk_reasons.length > 0 && (
                      <p>Motivos: {activeReview.risk_reasons.join(", ")}. Requiere revisión humana cuidadosa antes de publicar.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="rep-response-section">
                {serverPublicationNeedsReview && !uncertainPublication && <div className="rep-warning-box" role="status">
                  <div><strong>Publicación con error registrado</strong><p>La última publicación de esta respuesta necesita comprobación. No vuelvas a enviar hasta verificar el estado real en Google.</p></div>
                  <button type="button" disabled={busy} onClick={reload}>Volver a comprobar</button>
                </div>}
                {uncertainPublication && <div className="rep-warning-box" role="status">
                  <div><strong>Publicación sin confirmar</strong><p>{uncertainPublication}</p></div>
                  <button type="button" disabled={busy} onClick={reload}>Actualizar estado</button>
                </div>}
                <div className="rep-response-heading">
                  <h3>Respuesta del negocio</h3>
                  {editableResponse && <ResponseStatusBadge status={editableResponse.status} />}
                </div>

                {editableResponse && (!reviewPublished || editableResponse.status === "publicada") ? (
                  <form onSubmit={saveResponseEdit} className="rep-response-form">
                    <label htmlFor="active-reply-body">{reviewPublished ? "Respuesta registrada" : "Borrador de respuesta"}</label>
                    <textarea
                      id="active-reply-body"
                      name="body"
                      value={draft.identity === draftIdentity ? draft.value : editableResponse.body}
                      rows={5}
                      disabled={busy || reviewPublished}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setDraft((current) => ({
                          identity: draftIdentity,
                          baseline: current.identity === draftIdentity ? current.baseline : editableResponse.body,
                          value: nextValue,
                        }));
                      }}
                    />
                    <div className="rep-response-meta">
                      <span className="rep-version-tag">
                        v{editableResponse.version} · {editableResponse.created_by === "ia" ? "generada por IA" : "editada"}
                      </span>
                      <span className="rep-disclaimer">
                        La IA propone borradores; vos decidís qué publicar. Al publicar una corrección, el agente puede aprender ese estilo si la memoria está activada.
                      </span>
                    </div>

                    {draftDirty && !reviewPublished && (
                      <div className="settings-note" role="status">
                        <div>
                          <strong>Cambios sin guardar</strong>
                          <span>Esta edición queda protegida mientras trabajás en la reseña.</span>
                        </div>
                        <button type="button" disabled={busy} onClick={resetLocalDraft}>
                          Descartar cambios locales
                        </button>
                      </div>
                    )}

                    {editableResponse.status === "sugerida" && !reviewPublished && (
                      <div className="rep-quick-actions-bar">
                        <span className="rep-quick-label">Ajuste rápido:</span>
                        <button
                          type="button"
                          disabled={busy || draftDirty}
                          onClick={() => reviseDraft(editableResponse.id, "Hacé la respuesta más breve, de no más de dos oraciones.")}
                        >
                          Más breve
                        </button>
                        <button
                          type="button"
                          disabled={busy || draftDirty}
                          onClick={() => reviseDraft(editableResponse.id, "Hacé la respuesta con un tono más cálido y cercano.")}
                        >
                          Más cálida
                        </button>
                        <button
                          type="button"
                          disabled={busy || draftDirty}
                          onClick={() => reviseDraft(editableResponse.id, "Hacé la respuesta con un tono más formal y profesional.")}
                        >
                          Más profesional
                        </button>
                        <button
                          type="button"
                          disabled={busy || draftDirty}
                          onClick={() => setCustomModalOpen(true)}
                        >
                          Personalizar…
                        </button>
                      </div>
                    )}

                    <div className="rep-action-buttons rep-actions-task-first">
                      {!reviewPublished && (
                        <>
                          {draftDirty ? (
                            <button
                              type="submit"
                              className="primary-button"
                              disabled={busy || !draft.value.trim()}
                            >
                              {busy ? "Guardando cambios…" : "Guardar edición"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="primary-button"
                              disabled={busy || !!uncertainPublication || !!serverPublicationNeedsReview || !googleConnected || draft.identity !== draftIdentity || !draft.value.trim()}
                              onClick={() => requestPublishConfirmation(editableResponse.id, draft.value)}
                            >
                              Revisar y publicar
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => copyToClipboard(draft.value)}
                          >
                            Copiar texto
                          </button>
                        </>
                      )}

                      {!googleConnected && !reviewPublished && (
                        <p className="rep-hint-text">
                          Conectá Google Business Profile desde Configuración para habilitar el flujo de publicación.
                        </p>
                      )}

                      {googleConnected && !reviewPublished && (
                        <p className="rep-hint-text rep-publish-policy-note">
                          Para publicar desde esta pantalla, revisá el texto final y confirmá expresamente cada envío a Google.
                        </p>
                      )}

                      {reviewPublished && (
                        <div className="rep-published-actions">
                          <div>
                            <strong>Respuesta publicada</strong>
                            <span>
                              {editableResponse.published_at
                                ? `Publicada el ${formatDate(editableResponse.published_at)}.`
                                : "La respuesta ya figura como publicada."}
                            </span>
                          </div>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => copyToClipboard(draft.value)}
                          >
                            Copiar respuesta
                          </button>
                        </div>
                      )}
                    </div>

                    {!reviewPublished && (
                      <div className="rep-warning-box">
                        <div>
                          <strong>Descartar este borrador</strong>
                          <p>Usalo sólo si esta versión ya no sirve. La acción requiere confirmación.</p>
                        </div>
                        <button
                          type="button"
                          className="danger-button"
                          disabled={busy}
                          onClick={() => setRejectModalOpen(true)}
                        >
                          Descartar borrador
                        </button>
                      </div>
                    )}
                  </form>
                ) : reviewPublished ? (
                  <div className="rep-no-draft-box" role="status">
                    <h3>Esta reseña ya tiene respuesta en Google</h3>
                    <p>El texto público no está verificado en este detalle. No es necesario generar ni publicar otra respuesta.</p>
                  </div>
                ) : (
                  <div className="rep-no-draft-box">
                    <div className="rep-no-draft-copy">
                      <span className="rep-command-kicker">Próximo paso</span>
                      <h3>Prepará una respuesta</h3>
                    </div>
                    <p>
                      {isDiscarded
                        ? "La versión anterior fue descartada. Podés generar un nuevo borrador asistido por IA."
                        : "Todavía no hay un borrador para esta reseña."}
                    </p>
                    <div className="rep-generate-row">
                      <label className="rep-tone-select">
                        <span>Tono deseado:</span>
                        <select
                          value={selectedTone}
                          onChange={(e) => setSelectedTone(e.target.value)}
                          disabled={busy}
                        >
                          <option value="profesional">Profesional</option>
                          <option value="cercano">Cercano</option>
                          <option value="formal">Formal</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={busy}
                        onClick={() => generateDraft(activeReview.id)}
                      >
                        {busy ? "Generando borrador…" : "Generar respuesta con IA"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <details className="rep-review-context-disclosure">
                <summary>
                  <span>Detalles del proceso y análisis</span>
                  <span className="rep-hint-text">Etapas, sentimiento y temas de la reseña</span>
                </summary>
                <div className="rep-review-context-body">
              <div className="rep-response-journey" aria-label="Etapas de respuesta">
                <div className="done">
                  <span>1</span>
                  <strong>Reseña</strong>
                </div>
                <div className={responseJourneyStage === "draft" ? "current" : "done"}>
                  <span>2</span>
                  <strong>Borrador</strong>
                </div>
                <div className={responseJourneyStage === "review" ? "current" : responseJourneyStage === "published" ? "done" : ""}>
                  <span>3</span>
                  <strong>Revisión humana</strong>
                </div>
                <div className={responseJourneyStage === "published" ? "done" : ""}>
                  <span>4</span>
                  <strong>Publicado</strong>
                </div>
              </div>

              {(activeReview.ai_summary || activeReview.sentiment || (activeReview.topics && activeReview.topics.length > 0)) && (
                <div className="rep-analysis-box">
                  <h3>Análisis del Agente</h3>
                  {activeReview.ai_summary && <p className="rep-analysis-summary">{activeReview.ai_summary}</p>}
                  <div className="rep-analysis-tags">
                    <SentimentBadge sentiment={activeReview.sentiment} />
                    {activeReview.topics?.map((topic, i) => {
                      const label = typeof topic === "string" ? topic : topic.label || topic.topic || "";
                      return <span key={i} className="rep-topic-tag">{label}</span>;
                    })}
                  </div>
                </div>
              )}

                </div>
              </details>
            </div>
          ) : (
            <div className="empty-state" role="status">
              <p>{selectedId
                ? "Esta reseña no se encontró. Volvé a la bandeja para elegir otra."
                : "Elegí una reseña de la lista para ver su detalle y responder."}</p>
            </div>
          )}
        </section>
      </div>

      {publishConfirmation && (
        <div className="rep-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="confirm-publish-title">
          <div className="rep-modal-card">
            <div className="rep-modal-head">
              <h2 id="confirm-publish-title">Confirmar publicación en Google</h2>
              <button type="button" onClick={() => setPublishConfirmation(null)} aria-label="Cerrar modal">×</button>
            </div>
            <p>Vas a publicar esta respuesta en la reseña de <strong>{activeReview?.author || "este cliente"}</strong>. Verificá el texto completo:</p>
            <blockquote style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{publishConfirmation.body}</blockquote>
            <p className="rep-hint-text">Esta acción publica una respuesta pública en Google para la reseña seleccionada. No se publica ningún texto sin tu confirmación expresa.</p>
            <div className="button-row" style={{ marginTop: "14px" }}>
              <button type="button" className="primary-button" disabled={busy} onClick={confirmManualPublication}>
                Confirmar y publicar
              </button>
              <button type="button" data-safe-focus disabled={busy} onClick={() => setPublishConfirmation(null)}>
                Volver a editar
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingContextChange && (
        <div className="rep-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="dirty-context-title">
          <div className="rep-modal-card">
            <div className="rep-modal-head">
              <h2 id="dirty-context-title">Hay cambios sin guardar</h2>
              <button type="button" onClick={() => setPendingContextChange(null)} aria-label="Cerrar modal">×</button>
            </div>
            <p>
              Si cambiás de reseña o filtro ahora, la edición local de esta respuesta se descarta.
            </p>
            <div className="button-row" style={{ marginTop: "14px" }}>
              <button
                type="button"
                className="danger-button"
                onClick={abandonLocalDraftAndContinue}
              >
                Descartar cambios y continuar
              </button>
              <button type="button" data-safe-focus onClick={() => setPendingContextChange(null)}>
                Seguir editando
              </button>
            </div>
          </div>
        </div>
      )}

      {customModalOpen && editableResponse && (
        <div className="rep-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="custom-modal-title">
          <div className="rep-modal-card">
            <div className="rep-modal-head">
              <h2 id="custom-modal-title">Mejorar respuesta con IA</h2>
              <button type="button" onClick={() => setCustomModalOpen(false)} aria-label="Cerrar modal">×</button>
            </div>
            <label htmlFor="custom-instruction">¿Qué te gustaría ajustar de la respuesta?</label>
            <textarea
              id="custom-instruction"
              rows={3}
              placeholder="Ej: mencionar que los domingos abrimos hasta las 20hs"
              value={customInstruction}
              onChange={(e) => setCustomInstruction(e.target.value)}
            />
            <div className="button-row" style={{ marginTop: "14px" }}>
              <button
                type="button"
                className="primary-button"
                disabled={busy || !customInstruction.trim()}
                onClick={() => {
                  reviseDraft(editableResponse.id, customInstruction.trim());
                  setCustomModalOpen(false);
                  setCustomInstruction("");
                }}
              >
                Generar ajuste
              </button>
              <button type="button" onClick={() => setCustomModalOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModalOpen && editableResponse && (
        <div className="rep-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="reject-modal-title">
          <div className="rep-modal-card">
            <div className="rep-modal-head">
              <h2 id="reject-modal-title">Descartar borrador</h2>
              <button type="button" onClick={() => setRejectModalOpen(false)} aria-label="Cerrar modal">×</button>
            </div>
            <label htmlFor="reject-note">¿Por qué descartás este borrador? (opcional)</label>
            <textarea
              id="reject-note"
              rows={3}
              placeholder="Ej: no refleja el nuevo menú o requiere otro enfoque"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <div className="button-row" style={{ marginTop: "14px" }}>
              <button
                type="button"
                className="danger-button"
                disabled={busy}
                onClick={() => rejectDraft(editableResponse.id)}
              >
                Confirmar descarte
              </button>
              <button type="button" data-safe-focus onClick={() => setRejectModalOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
